import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle, FileText, Loader2, Lock, Eye, Pencil, MessageSquare } from 'lucide-react';
import { request } from '../api/client';

interface ShareInfo {
  needPassword?: boolean;
  slug?: string;
  docId?: string;
  title: string;
  emoji: string | null;
  type: 'doc' | 'mindmap';
  workspaceName: string;
  permission: 'view' | 'comment' | 'edit';
  content?: string;
}

const PERMISSION_META: Record<string, { label: string; icon: typeof Eye }> = {
  view: { label: '只读', icon: Eye },
  comment: { label: '可评论', icon: MessageSquare },
  edit: { label: '可编辑', icon: Pencil },
};

export function SharePage() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [verifiedData, setVerifiedData] = useState<ShareInfo | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['share', token],
    queryFn: () => request<ShareInfo>(`/share/${token}`),
    enabled: !!token,
  });

  const verifyMutation = useMutation({
    mutationFn: (pwd: string) =>
      request<ShareInfo>(`/share/${token}/verify`, {
        method: 'POST',
        body: JSON.stringify({ password: pwd }),
      }),
    onSuccess: (result) => {
      setVerifiedData(result);
      setPassword('');
    },
    onError: () => {
      // 错误由 mutation.data 状态处理
    },
  });

  // 需要密码且尚未验证
  if (data?.needPassword && !verifiedData) {
    return <PasswordView password={password} setPassword={setPassword} onSubmit={() => verifyMutation.mutate(password)} error={verifyMutation.isError} loading={verifyMutation.isPending} />;
  }

  const info = verifiedData || data;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-subtle px-4">
        <div className="w-full max-w-md rounded-2xl bg-bg-surface p-8 text-center shadow-sm ring-1 ring-black/5">
          <AlertCircle className="mx-auto h-12 w-12 text-danger" />
          <h1 className="mt-4 text-xl font-semibold">无法访问</h1>
          <p className="mt-2 text-sm text-text-secondary">
            {error instanceof Error ? error.message : '分享链接无效或已过期'}
          </p>
          <Link to="/" className="btn btn-secondary btn-md mt-6 inline-flex">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const permMeta = PERMISSION_META[info.permission] || PERMISSION_META.view;
  const PermIcon = permMeta.icon;

  return (
    <div className="min-h-screen bg-bg-base">
      {/* 顶部信息栏 */}
      <header className="sticky top-0 z-10 border-b border-border bg-bg-surface/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-1.5 text-sm text-text-tertiary transition-colors hover:text-text-primary">
            <span className="text-base font-semibold text-text-primary">comind</span>
          </Link>
          <div className="flex items-center gap-3 text-xs text-text-tertiary">
            <span>{info.workspaceName}</span>
            <span className="flex items-center gap-1 rounded-full bg-bg-subtle px-2.5 py-1 font-medium text-text-secondary">
              <PermIcon className="h-3 w-3" />
              {permMeta.label}
            </span>
          </div>
        </div>
      </header>

      {/* 文档内容 */}
      <main className="mx-auto max-w-3xl px-6 py-12">
        {/* 文档标题 */}
        <div className="mb-8 border-b border-border pb-6">
          <div className="flex items-center gap-3">
            {info.emoji ? (
              <span className="text-3xl">{info.emoji}</span>
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-50 shadow-sm ring-1 ring-accent/20">
                <FileText className="h-6 w-6 text-accent" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-text-primary">
              {info.title || '未命名文档'}
            </h1>
          </div>
        </div>

        {/* 文档正文 */}
        {info.type === 'mindmap' ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <FileText className="h-12 w-12 text-text-tertiary" />
            <p className="mt-4 text-sm text-text-secondary">思维导图请在登录后查看</p>
            <Link
              to={`/login?redirect=/space/${info.docId}`}
              className="btn btn-primary btn-md mt-6 inline-flex"
            >
              登录后查看
            </Link>
          </div>
        ) : info.content ? (
          <div
            className="ProseMirror share-content"
            dangerouslySetInnerHTML={{ __html: info.content }}
          />
        ) : (
          <p className="py-20 text-center text-sm text-text-tertiary">文档暂无内容</p>
        )}

        {/* 底部操作 */}
        {info.permission !== 'view' && (
          <div className="mt-12 border-t border-border pt-6 text-center">
            <Link
              to={`/login?redirect=/space/${info.docId}`}
              className="btn btn-secondary btn-md inline-flex"
            >
              登录后{info.permission === 'edit' ? '编辑' : '评论'}
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

/** 密码验证视图 */
function PasswordView({
  password,
  setPassword,
  onSubmit,
  error,
  loading,
}: {
  password: string;
  setPassword: (v: string) => void;
  onSubmit: () => void;
  error: boolean;
  loading: boolean;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-subtle px-4">
      <div className="w-full max-w-sm rounded-2xl bg-bg-surface p-8 shadow-sm ring-1 ring-black/5">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-50 text-accent">
          <Lock className="h-6 w-6" />
        </div>
        <h1 className="mt-5 text-center text-lg font-semibold">需要密码</h1>
        <p className="mt-1.5 text-center text-sm text-text-secondary">
          请输入密码以查看此文档
        </p>
        <form
          className="mt-6 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <input
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="输入密码"
            autoFocus
          />
          {error && (
            <p className="text-sm text-danger">密码错误，请重试</p>
          )}
          <button
            type="submit"
            disabled={!password || loading}
            className="btn btn-primary btn-md w-full"
          >
            {loading ? '验证中…' : '查看文档'}
          </button>
        </form>
      </div>
    </div>
  );
}
